import { Route } from "@angular/router";
import { initialDataResolver } from "app/app.resolvers";
import { AuthGuard } from "app/core/auth/guards/auth.guard";
import { NoAuthGuard } from "app/core/auth/guards/noAuth.guard";
import { LayoutComponent } from "app/layout/layout.component";
import { AdminCollaboratorGuard } from "./core/navigation/guards/AdminCollaborator.guard";
import { AdminGuard } from "./core/navigation/guards/Admin.guard";
import { getCategoryTypeResolver } from "./modules/admin/users/resolvers/category-type.resolver";

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect empty path to '/example'
  { path: "", pathMatch: "full", redirectTo: "home" },

  // Redirect signed-in user to the '/posts'
  //
  // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: "signed-in-redirect", pathMatch: "full", redirectTo: "home" },

  // Auth routes for guests
  {
    path: "",
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "confirmation-required",
        loadChildren: () =>
          import(
            "app/modules/auth/confirmation-required/confirmation-required.routes"
          ),
      },
      {
        path: "forgot-password",
        loadChildren: () =>
          import("app/modules/auth/forgot-password/forgot-password.routes"),
      },
      {
        path: "reset-password",
        loadChildren: () =>
          import("app/modules/auth/reset-password/reset-password.routes"),
      },
      {
        path: "sign-in",
        loadChildren: () => import("app/modules/auth/sign-in/sign-in.routes"),
      },
      /*{
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.routes'),
            },*/
    ],
  },

  // Auth routes for authenticated users
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "sign-out",
        loadChildren: () => import("app/modules/auth/sign-out/sign-out.routes"),
      },
      {
        path: "unlock-session",
        loadChildren: () =>
          import("app/modules/auth/unlock-session/unlock-session.routes"),
      },
    ],
  },

  // Landing routes
  /*{
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.routes'),
            },
        ],
    },*/

  {
    path: "",
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      {
        path: "error",
        loadChildren: () => import("app/modules/error/error.routes"),
      },
    ],
  },
  // Admin routes
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      {
        path: "home",
        canActivate: [AdminCollaboratorGuard],
        loadChildren: () => import("app/modules/landing/home/home.routes"),
      },
      {
        path: "posts",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () => import("app/modules/admin/posts/posts.routes"),
      },
      {
        path: "resources",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/resources/resources.routes"),
      },
      {
        path: "resource-download",
        canActivate: [AdminGuard],
        canActivateChild: [AdminGuard],
        loadChildren: () =>
          import(
            "app/modules/admin/resource-download/resource-download.routes"
          ),
      },
      {
        path: "users",
        canActivate: [AdminGuard],
        canActivateChild: [AdminGuard],
        loadChildren: () => import("app/modules/admin/users/users.routes"),
      },
      {
        path: "roles",
        canActivate: [AdminGuard],
        canActivateChild: [AdminGuard],
        loadChildren: () => import("app/modules/admin/roles/roles.routes"),
      },
      {
        path: "subscriptions",
        canActivate: [AdminGuard],
        canActivateChild: [AdminGuard],
        loadChildren: () =>
          import("app/modules/admin/subscription/subscription.routes"),
      },
      {
        path: "categories",
        resolve: {
          type: getCategoryTypeResolver,
        },
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/categories/categories.routes"),
      },
      {
        path: "pages",
        resolve: {
          type: getCategoryTypeResolver,
        },
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () => import("app/modules/admin/categories/pages.routes"),
      },
      {
        path: "challenges",
        resolve: {
          type: getCategoryTypeResolver,
        },
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/categories/challenges.routes"),
      },
      {
        path: "questions",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/questions/questions.routes"),
      },
      {
        path: "onboarding/pages",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/page-questions/page-questions.routes"),
      },
      {
        path: "answers",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () => import("app/modules/admin/answers/answers.routes"),
      },
      {
        path: "insignias",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/insignias/insignias.routes"),
      },

      {
        path: "subcategories",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/sub-categories/subcategories.routes"),
      },

      {
        path: "post-types",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/post-type/post-type.routes"),
      },
      {
        path: "tags",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () => import("app/modules/admin/tags/tags.routes"),
      },
      {
        path: "tiers",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () => import("app/modules/admin/tiers/tiers.routes"),
      },
      {
        path: "notifications",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/notifications/notifications.routes"),
      },
      {
        path: "programs",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/programs/programs.routes"),
      },
      {
        path: "feedback",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/feedback/feedback.routes"),
      },
      {
        path: "sections",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/sections/sections.routes"),
      },
      {
        path: "template",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/template/template.routes"),
      },
      {
        path: "page-category",
        resolve: {
          type: getCategoryTypeResolver,
        },
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/page-category/page-category.routes"),
      },
      {
        path: "synchronization",
        canActivate: [AdminGuard],
        canActivateChild: [AdminGuard],
        loadChildren: () =>
          import("app/modules/admin/synchronization/synchronization.routes"),
      },
      {
        path: "menu-items",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import("app/modules/admin/menu-items/menu-items.routes"),
      },
      {
        path: "custom-section-types",
        canActivate: [AdminCollaboratorGuard],
        canActivateChild: [AdminCollaboratorGuard],
        loadChildren: () =>
          import(
            "app/modules/admin/custom-section-types/custom-section-types.routes"
          ),
      },
      {
        path: "operators",
        canActivate: [AdminGuard],
        canActivateChild: [AdminGuard],
        loadChildren: () =>
          import("app/modules/admin/operators/operators.routes"),
      },
      {
        path: "bps",
        canActivate: [AdminGuard],
        canActivateChild: [AdminGuard],
        loadChildren: () => import("app/modules/admin/bps/bps.routes"),
      },
      {
        path: "global-settings",
        canActivate: [AdminGuard],
        canActivateChild: [AdminGuard],
        loadChildren: () =>
          import("app/modules/admin/global-settings/global-settings.routes"),
      },
      {
        path: "try-and-buy",
        canActivate: [AdminGuard],
        canActivateChild: [AdminGuard],
        loadChildren: () =>
          import(
            "app/modules/admin/global-settings/pages/try-and-buy/try-and-buy.routes"
          ),
      },
    ],
  },
];
